// @flow

import React, { useMemo } from "react";
import * as R from "ramda";

import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  arrayMove,
  useSortable,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { VStack, HStack, Button, Text } from "@chakra-ui/react";
import { DragHandleIcon } from "@chakra-ui/icons";

import type { Stitch as StitchType } from "src/types";

import { DragHandle } from "src/components/Unifize/DragHandle";
import Icon from "src/icons";
import Stitch from "./Stitch";
import * as styles from "./styles";

const SortableItem = ({ id, item, onUpdate, onRemove }) => {
  const { listeners, setNodeRef, setActivatorNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <HStack ref={setNodeRef} style={style} {...styles.SortableItem}>
      <DragHandle
        dragHandleRef={setActivatorNodeRef}
        dragHandleListeners={listeners}
        icon={<DragHandleIcon {...styles.DragHandle} />}
      />
      <Stitch data={item} onUpdate={onUpdate} />
      <Icon
        type="deleteTrash"
        onClick={() => onRemove(item.seqNo)}
        style={styles.DeleteIcon}
      />
    </HStack>
  );
};

type Props = {
  stitches: StitchType[],
  setStitches: Function,
};

export default function Stitches({ stitches, setStitches }: Props) {
  const itemIds = useMemo(
    () => stitches.map((stitch) => stitch.seqNo),
    [stitches],
  );

  const addItem = () => {
    setStitches((prevStitches) => {
      const nextSeqNo =
        prevStitches.length > 0
          ? prevStitches[prevStitches.length - 1].seqNo + 1
          : 1;
      return R.append(
        {
          seqNo: nextSeqNo,
          templateId: null,
        },
        prevStitches,
      );
    });
  };

  const removeItem = (id) => {
    setStitches(R.reject(R.propEq("seqNo", id)));
    setStitches((prevStitches) =>
      prevStitches.map((item, index) => ({
        ...item,
        seqNo: (item.seqNo = index + 1),
      })),
    );
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;

    const oldIndex = itemIds.indexOf(active.id);
    const newIndex = itemIds.indexOf(over.id);

    setStitches((prevItems) =>
      arrayMove(prevItems, oldIndex, newIndex).map((item, itemIndex) => ({
        ...item,
        seqNo: itemIndex + 1,
      })),
    );
  };

  const updateItem = (id, data) => {
    setStitches((prevItems) => {
      return prevItems.map((item) => {
        if (item.seqNo === id) {
          return data;
        }
        return item;
      });
    });
  };

  if (R.isEmpty(stitches)) {
    return (
      <HStack {...styles.EmptyContainer}>
        <Text variant="inkLightestSm">(Click on Stitch to create a PDF)</Text>

        <Button onClick={addItem} variant="transparentLink">
          + Stitch
        </Button>
      </HStack>
    );
  }

  return (
    <VStack {...styles.StitchesContainer}>
      <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
          {stitches.map((item) => (
            <SortableItem
              key={item.seqNo}
              id={item.seqNo}
              item={item}
              onUpdate={updateItem}
              onRemove={removeItem}
            />
          ))}
        </SortableContext>
      </DndContext>

      <Button onClick={addItem} variant="transparentLink">
        + Stitch
      </Button>
    </VStack>
  );
}
